import React from 'react';
import { cn } from '../lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, Button } from '@ai-content-hub/content-kit';

export interface NavigationButtonsProps {
    onPrevious?: () => void;
    onNext?: () => void;
    onSubmit?: () => void;
    onRetry?: () => void;

    canPrevious?: boolean;
    canNext?: boolean;
    canSubmit?: boolean;
    canRetry?: boolean;

    showPrevious?: boolean;
    showNext?: boolean;
    showSubmit?: boolean;
    showRetry?: boolean;

    isSubmitting?: boolean;

    /** Shown when submit is visible but disabled (e.g. incomplete answers). */
    submitDisabledHint?: string;

    /** Optional leading slot (e.g. question index buttons), centered in the nav row. */
    leading?: React.ReactNode;
    /** Bumps when leading content should refresh (e.g. answer-state fingerprint). */
    leadingKey?: string;

    labels?: {
        previous?: string;
        next?: string;
        submit?: string;
        retry?: string;
        submitting?: string;
    };

    className?: string;
    variant?: 'default' | 'compact';
}

/**
 * Footer leading slot may host question-set nav buttons whose state classes are defined
 * in content-types (not scanned by the editor embed Tailwind build). Mirror those
 * utilities here so answered / visited / graded states stay visible in the footer.
 */
const navigationButtonsLeadingQuestionNavClass =
    '[&_.question-set-player-tab--answered]:!border-success/60 [&_.question-set-player-tab--answered]:!bg-success/15 [&_.question-set-player-tab--answered]:text-foreground [&_.question-set-player-tab--answered]:shadow-none [&_.question-set-player-tab--answered:hover]:!bg-success/25 [&_.question-set-player-tab--visited-unanswered]:!border-warning/60 [&_.question-set-player-tab--visited-unanswered]:!bg-warning/15 [&_.question-set-player-tab--visited-unanswered]:text-foreground [&_.question-set-player-tab--visited-unanswered]:shadow-none [&_.question-set-player-tab--visited-unanswered:hover]:!bg-warning/25 [&_.question-set-player-tab--graded-correct]:!border-success/60 [&_.question-set-player-tab--graded-correct]:!bg-success/15 [&_.question-set-player-tab--graded-correct]:text-foreground [&_.question-set-player-tab--graded-correct]:shadow-none [&_.question-set-player-tab--graded-correct:hover]:!bg-success/25 [&_.question-set-player-tab--graded-incorrect]:!border-destructive/60 [&_.question-set-player-tab--graded-incorrect]:!bg-destructive/15 [&_.question-set-player-tab--graded-incorrect]:text-foreground [&_.question-set-player-tab--graded-incorrect]:shadow-none [&_.question-set-player-tab--graded-incorrect:hover]:!bg-destructive/25';

/**
 * NavigationButtons - Navigation controls for content
 */
export function NavigationButtons({
    onPrevious,
    onNext,
    onSubmit,
    onRetry,
    canPrevious = true,
    canNext = true,
    canSubmit = true,
    canRetry = false,
    showPrevious = true,
    showNext = true,
    showSubmit = true,
    showRetry = false,
    isSubmitting = false,
    submitDisabledHint,
    leading,
    leadingKey,
    labels = {},
    className,
    variant = 'default',
}: NavigationButtonsProps): React.ReactElement {
    const { previous = 'Previous', next = 'Next', submit = 'Submit', retry = 'Try Again', submitting = 'Submitting...' } = labels;

    // EAA-1679: switched these from raw <button>s to content-kit's Button so the
    // player's nav controls actually carry data-slot="button" — skin-playful.css
    // only keys off that attribute, so a tenant's Playful skin was silently a
    // no-op on the one button every learner interacts with (Submit/Next/Retry),
    // even though the skin/theme data itself was resolving and applying fine.
    const navSize = variant === 'compact' ? 'sm' : 'default';

    return (
        <div className={cn('navigation-buttons', 'flex w-full min-h-10 items-center gap-3', className)}>
            {showPrevious && (
                <div className="navigation-buttons-previous-wrap relative z-10 shrink-0">
                    <Button
                        type="button"
                        variant="secondary"
                        size={navSize}
                        onClick={onPrevious}
                        disabled={!canPrevious}
                        className="navigation-buttons-previous max-md:h-11"
                    >
                        {previous}
                    </Button>
                </div>
            )}

            {leading != null ? (
                <div
                    className={cn(
                        'navigation-buttons-leading relative min-h-9 min-w-0 flex-1 self-stretch',
                        navigationButtonsLeadingQuestionNavClass
                    )}
                >
                    <div
                        key={leadingKey ?? 'navigation-buttons-leading-slot'}
                        className="navigation-buttons-leading-inner py-2 absolute left-0 right-0 top-1/2 z-[1] flex -translate-y-1/2 justify-center overflow-x-auto px-1"
                    >
                        {leading}
                    </div>
                </div>
            ) : (
                <div className="flex-1" />
            )}

            <div className="navigation-buttons-actions relative z-10 flex shrink-0 items-center gap-3">
                {/* Retry */}
                {showRetry && canRetry && (
                    <Button
                        type="button"
                        variant="warning"
                        size={navSize}
                        onClick={onRetry}
                        className="navigation-buttons-retry max-md:h-11"
                    >
                        {retry}
                    </Button>
                )}

                {/* Submit */}
                {showSubmit &&
                    (() => {
                        const submitDisabled = !canSubmit || isSubmitting;
                        const showDisabledTooltip =
                            Boolean(submitDisabledHint) && submitDisabled && !isSubmitting;
                        const submitButton = (
                            <Button
                                type="button"
                                variant="default"
                                size={navSize}
                                onClick={onSubmit}
                                disabled={submitDisabled}
                                className="navigation-buttons-submit max-md:h-11" /* 44px touch target on mobile; desktop keeps the size variant */
                            >
                                {isSubmitting ? submitting : submit}
                            </Button>
                        );
                        if (!showDisabledTooltip) {
                            return submitButton;
                        }
                        return (
                            <TooltipProvider delayDuration={300}>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <span className="inline-flex cursor-pointer">
                                            {submitButton}
                                        </span>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" sideOffset={6}>
                                        {submitDisabledHint}
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        );
                    })()}

                {/* Next */}
                {showNext && (
                    <Button
                        type="button"
                        variant="default"
                        size={navSize}
                        onClick={onNext}
                        disabled={!canNext}
                        className="navigation-buttons-next max-md:h-11"
                    >
                        {next}
                    </Button>
                )}
            </div>
        </div>
    );
}

/**
 * Simple action button
 */
export interface ActionButtonProps {
    onClick?: () => void;
    disabled?: boolean;
    loading?: boolean;
    variant?: 'primary' | 'secondary' | 'success' | 'danger' | 'ghost';
    size?: 'sm' | 'md' | 'lg';
    children: React.ReactNode;
    className?: string;
    type?: 'button' | 'submit' | 'reset';
}

// EAA-1679: maps ActionButton's own variant/size vocabulary onto content-kit
// Button's — see the NavigationButtons fix above for why (data-slot="button"
// is required for skin-playful.css to ever match this control).
const kitVariantMap = {
    primary: 'default',
    secondary: 'secondary',
    success: 'success',
    danger: 'destructive',
    ghost: 'ghost',
} as const;

const kitSizeMap = {
    sm: 'sm',
    md: 'default',
    lg: 'lg',
} as const;

export function ActionButton({
    onClick,
    disabled = false,
    loading = false,
    variant = 'primary',
    size = 'md',
    children,
    className,
    type = 'button',
}: ActionButtonProps): React.ReactElement {
    return (
        <Button
            type={type}
            variant={kitVariantMap[variant]}
            size={kitSizeMap[size]}
            onClick={onClick}
            disabled={disabled || loading}
            className={cn('action-button', className)}
        >
            {loading ? (
                <span className="flex items-center gap-2">
                    <span className="animate-spin">⟳</span>
                    Loading...
                </span>
            ) : (
                children
            )}
        </Button>
    );
}
